import React, { useState } from 'react';
import {
  Box, Typography, TextField, MenuItem, Button, Paper, CircularProgress, Alert, Container,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import axios from 'axios';
import { API_BASE_URL } from '../../constants/constants';

const DeleteAccountPublic = () => {
  const [form, setForm] = useState({ accountType: '', name: '', email: '', mobile: '', reason: '' });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (field) => (e) => {
    setForm((prev) => ({ ...prev, [field]: e.target.value }));
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!form.accountType) { setError('Please select account type'); return; }
    if (!form.name.trim()) { setError('Please enter your name'); return; }
    if (!form.mobile.trim() || !/^[6-9]\d{9}$/.test(form.mobile.trim())) {
      setError('Please enter a valid 10-digit Indian mobile number');
      return;
    }

    setLoading(true);
    try {
      const res = await axios.post(`${API_BASE_URL}/account-deletion/request`, {
        accountType: form.accountType,
        name: form.name.trim(),
        email: form.email.trim(),
        mobile: form.mobile.trim(),
        reason: form.reason.trim(),
      });

      if (res.data.success) {
        setSuccess(true);
      } else {
        setError(res.data.message || 'Failed to submit request');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to submit request. Please try again.');
    }
    setLoading(false);
  };

  if (success) {
    return (
      <Container maxWidth="sm">
        <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Paper sx={{ p: 4, textAlign: 'center', borderRadius: 3 }}>
            <CheckCircleIcon sx={{ fontSize: 64, color: '#2E7D32', mb: 2 }} />
            <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 1 }}>Request Submitted</Typography>
            <Typography color="text.secondary" sx={{ mb: 2 }}>
              Your account deletion request has been submitted successfully. Our team will verify your identity and process the request within 7 working days.
            </Typography>
            <Typography variant="body2" color="text.secondary">
              You will be notified at your registered contact once the request is processed. As per PMLA compliance, transaction records will be retained for 5 years even after account deletion.
            </Typography>
            <Typography variant="body2" sx={{ mt: 2, fontWeight: 'bold' }}>
              For queries: support@mythrismartbazaar.com
            </Typography>
          </Paper>
        </Box>
      </Container>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: '#F8FAFC', py: 4 }}>
      <Container maxWidth="sm">
        <Paper sx={{ p: 4, borderRadius: 3 }}>
          <Box sx={{ textAlign: 'center', mb: 3 }}>
            <DeleteIcon sx={{ fontSize: 48, color: '#D32F2F', mb: 1 }} />
            <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#0F172A' }}>
              Request Account Deletion
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Q-Pay by Mythri Smart Bazaar Private Limited
            </Typography>
          </Box>

          <Alert severity="info" sx={{ mb: 3 }}>
            Submit this form to request deletion of your Q-Pay account. Our team will verify your identity and process your request within 7 working days. Transaction records are retained for 5 years as per PMLA (Prevention of Money Laundering Act) compliance.
          </Alert>

          <form onSubmit={handleSubmit}>
            <TextField
              select fullWidth required label="Account Type" value={form.accountType}
              onChange={handleChange('accountType')} sx={{ mb: 2 }}
            >
              <MenuItem value="customer">Q-Pay Customer App</MenuItem>
              <MenuItem value="shop">Q-Pay Shop / Merchant App</MenuItem>
              <MenuItem value="agent">Q-Pay Agent App</MenuItem>
            </TextField>

            <TextField
              fullWidth required label="Full Name (as registered)"
              value={form.name} onChange={handleChange('name')} sx={{ mb: 2 }}
              placeholder="Enter your registered name"
            />

            <TextField
              fullWidth required label="Registered Mobile Number"
              value={form.mobile} onChange={handleChange('mobile')} sx={{ mb: 2 }}
              placeholder="10-digit mobile number" inputProps={{ maxLength: 10 }}
            />

            <TextField
              fullWidth label="Email Address (optional)"
              value={form.email} onChange={handleChange('email')} sx={{ mb: 2 }}
              placeholder="Your registered email"
            />

            <TextField
              fullWidth multiline rows={3} label="Reason for Deletion (optional)"
              value={form.reason} onChange={handleChange('reason')} sx={{ mb: 2 }}
              placeholder="Why do you want to delete your account?"
            />

            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

            <Button
              type="submit" variant="contained" fullWidth size="large"
              disabled={loading}
              sx={{
                backgroundColor: '#D32F2F', py: 1.5, fontWeight: 'bold', fontSize: 16,
                '&:hover': { backgroundColor: '#B71C1C' },
              }}
            >
              {loading ? <CircularProgress size={24} color="inherit" /> : 'Submit Deletion Request'}
            </Button>
          </form>

          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 3, textAlign: 'center' }}>
            By submitting this request, you acknowledge that your personal data will be permanently deleted or anonymized. Financial transaction records will be retained for 5 years as required by Indian law.
          </Typography>

          <Box sx={{ mt: 3, pt: 2, borderTop: '1px solid #E2E8F0', textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              <strong>Mythri Smart Bazaar Private Limited</strong>
            </Typography>
            <Typography variant="caption" color="text.secondary">
              support@mythrismartbazaar.com | https://mythrismartbazaar.com
            </Typography>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};

export default DeleteAccountPublic;
